import { useCallback, useEffect } from 'react';
import { useProjectContext } from '@gonfalon/app-context/useProjectContext';
import { useStorageSlot } from '@gonfalon/browser-storage/useStorageSlot';
import { getFeatureFlagQuery } from '@gonfalon/rest-api/queries/flags';
import { useQuery } from '@tanstack/react-query';

import type {
  AIConfigSDKChoice,
  OnboardingAiConfig,
  OnboardingAiConfigVariation,
  OnboardingFlag,
  OnboardingPath,
  OnboardingValues,
} from './types';

export const defaultOnboardingValues: OnboardingValues = {
  path: 'flags',
  sdk: 'react-client-sdk',
  flags: { createAFlag: { flagKey: '', flagName: '', flagOn: false } },
  experiments: { createAFlag: { flagKey: '', flagName: '', flagOn: false } },
  'ai-configs': {
    createAiConfig: { aiConfigName: '', aiConfigKey: '' },
    selectedSdkType: 'python',
    aiConfigVariation: { variationKey: '', variationName: '' },
  },
};

export const useOnboardingStorage = () => {
  const [currentString, slot] = useStorageSlot('onboarding', { storageType: 'sessionStorage' });
  const {
    context: { projectKey, selectedEnvironmentKey },
  } = useProjectContext();
  const current = currentString ? (JSON.parse(currentString) as OnboardingValues) : defaultOnboardingValues;

  const setOnboardingStorage = useCallback(
    (d: Partial<OnboardingValues>) => {
      slot.set(
        JSON.stringify({
          ...current, // include previous values
          ...d, // override with new values
        }),
      );
    },
    [current, slot],
  );

  const setCreateAFlag = useCallback(
    (path: OnboardingPath, flag: OnboardingFlag) => {
      const flagUpdate: { createAFlag: OnboardingFlag } = { createAFlag: flag };
      setOnboardingStorage({ [path]: flagUpdate });
    },
    [setOnboardingStorage],
  );

  const getCreateAFlag = (path: OnboardingPath): OnboardingFlag => {
    if (path !== 'observability' && current[path] && 'createAFlag' in current[path]) {
      return current[path].createAFlag as OnboardingFlag;
    }
    return { flagKey: '', flagName: '', flagOn: false };
  };

  const getCreateAiConfig = (path: OnboardingPath): OnboardingAiConfig | null => {
    if (path !== 'observability' && current[path] && 'createAiConfig' in current[path]) {
      return current[path].createAiConfig as OnboardingAiConfig;
    }
    return null;
  };

  const getAiConfigSDKChoice = (path: OnboardingPath): AIConfigSDKChoice | null => {
    if (path !== 'observability' && current[path] && 'selectedSdkType' in current[path]) {
      return current[path].selectedSdkType as AIConfigSDKChoice;
    }
    return null;
  };

  const getAiConfigVariation = (path: OnboardingPath): OnboardingAiConfigVariation | null => {
    if (path !== 'observability' && current[path] && 'aiConfigVariation' in current[path]) {
      return current[path].aiConfigVariation as OnboardingAiConfigVariation;
    }
    return null;
  };

  const getPath = (path: OnboardingPath): OnboardingValues[Exclude<OnboardingPath, 'observability'>] | null => {
    if (path !== 'observability' && current[path]) {
      return current[path];
    }
    return null;
  };

  const { data: flagForPath } = useQuery({
    enabled: !!getCreateAFlag(current.path).flagKey,
    ...getFeatureFlagQuery({
      projectKey,
      featureFlagKey: getCreateAFlag(current.path).flagKey,
      query: { env: [selectedEnvironmentKey] },
    }),
  });

  useEffect(() => {
    if (flagForPath?.archived) {
      setCreateAFlag(current.path, {
        flagKey: '',
        flagName: '',
        flagOn: false,
      });
    }
  }, [flagForPath, current.path, setCreateAFlag]);

  return {
    data: current,
    setOnboardingStorage,
    setCreateAFlag,
    getCreateAFlag,
    getCreateAiConfig,
    getAiConfigSDKChoice,
    getAiConfigVariation,
    getPath,
  };
};
